import React, { useState } from 'react';
import { Calendar, CheckCircle, XCircle, Clock, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const attendanceData = {
  January: {
    totalDays: 24,
    present: 22,
    absent: 1,
    late: 1,
    holidays: 7,
    days: [
      { date: 1, status: 'holiday', note: "New Year's Day" },
      { date: 2, status: 'present' },
      { date: 3, status: 'present' },
      { date: 4, status: 'weekend' },
      { date: 5, status: 'weekend' },
      { date: 6, status: 'present' },
      { date: 7, status: 'present' },
      { date: 8, status: 'present' },
      { date: 9, status: 'late', note: 'Arrived 15 mins late' },
      { date: 10, status: 'present' },
      { date: 11, status: 'weekend' },
      { date: 12, status: 'weekend' },
      { date: 13, status: 'present' },
      { date: 14, status: 'present' },
      { date: 15, status: 'absent', note: 'Sick leave' },
      { date: 16, status: 'present' },
      { date: 17, status: 'present' },
      { date: 18, status: 'weekend' },
      { date: 19, status: 'weekend' },
      { date: 20, status: 'present' },
      { date: 21, status: 'present' },
      { date: 22, status: 'present' },
      { date: 23, status: 'present' },
      { date: 24, status: 'present' },
      { date: 25, status: 'weekend' },
      { date: 26, status: 'holiday', note: 'Republic Day' },
      { date: 27, status: 'present' },
      { date: 28, status: 'present' },
      { date: 29, status: 'present' },
      { date: 30, status: 'present' },
      { date: 31, status: 'present' },
    ],
  },
};

const subjectAttendance = [
  { subject: 'Mathematics', total: 45, attended: 43, percentage: 95.5 },
  { subject: 'Physics', total: 40, attended: 38, percentage: 95.0 },
  { subject: 'Chemistry', total: 38, attended: 36, percentage: 94.7 },
  { subject: 'English', total: 42, attended: 40, percentage: 95.2 },
  { subject: 'Biology', total: 35, attended: 33, percentage: 94.3 },
  { subject: 'History', total: 30, attended: 29, percentage: 96.7 },
  { subject: 'Computer Science', total: 28, attended: 27, percentage: 96.4 },
];

const recentAttendance = [
  { date: 'Jan 17, 2026', day: 'Friday', status: 'present', checkIn: '07:55 AM', checkOut: '02:40 PM' },
  { date: 'Jan 16, 2026', day: 'Thursday', status: 'present', checkIn: '07:50 AM', checkOut: '02:35 PM' },
  { date: 'Jan 15, 2026', day: 'Wednesday', status: 'absent', checkIn: '-', checkOut: '-', note: 'Sick leave' },
  { date: 'Jan 14, 2026', day: 'Tuesday', status: 'present', checkIn: '07:52 AM', checkOut: '02:38 PM' },
  { date: 'Jan 13, 2026', day: 'Monday', status: 'present', checkIn: '07:48 AM', checkOut: '02:42 PM' },
];

const Attendance = () => {
  const [selectedMonth, setSelectedMonth] = useState('January');
  const currentMonthIndex = months.indexOf(selectedMonth);

  const goToPreviousMonth = () => {
    const newIndex = currentMonthIndex > 0 ? currentMonthIndex - 1 : 11;
    setSelectedMonth(months[newIndex]);
  };

  const goToNextMonth = () => {
    const newIndex = currentMonthIndex < 11 ? currentMonthIndex + 1 : 0;
    setSelectedMonth(months[newIndex]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'absent': return 'bg-red-500';
      case 'late': return 'bg-yellow-500';
      case 'holiday': return 'bg-blue-500';
      case 'weekend': return 'bg-gray-300';
      default: return 'bg-gray-200';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-700';
      case 'absent': return 'bg-red-100 text-red-700';
      case 'late': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const monthData = attendanceData[selectedMonth] || { totalDays: 0, present: 0, absent: 0, late: 0, days: [] };
  const attendancePercentage = monthData.totalDays > 0
    ? ((monthData.present / monthData.totalDays) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Attendance</h1>
          <p className="text-gray-600 mt-1">Track your attendance records and statistics</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Overall Attendance</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">92%</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <TrendingUp size={24} className="text-primary-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Present Days</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{monthData.present}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Absent Days</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{monthData.absent}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <XCircle size={24} className="text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Late Arrivals</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{monthData.late}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock size={24} className="text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">{selectedMonth} 2026</h2>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-gray-600">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="text-gray-600">Late</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-gray-600">Holiday</span>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {/* Empty cells for days before the 1st */}
            {[...Array(3)].map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square"></div>
            ))}
            {monthData.days?.map((day) => (
              <div
                key={day.date}
                className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer transition-all hover:scale-105 ${
                  day.status === 'present' ? 'bg-green-100 text-green-700' :
                  day.status === 'absent' ? 'bg-red-100 text-red-700' :
                  day.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                  day.status === 'holiday' ? 'bg-blue-100 text-blue-700' :
                  day.status === 'weekend' ? 'bg-gray-100 text-gray-400' :
                  'bg-gray-50 text-gray-400'
                }`}
                title={day.note || day.status}
              >
                {day.date}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Attendance & Subject-wise */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Attendance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Attendance</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentAttendance.map((record, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{record.date}</p>
                    <p className="text-sm text-gray-500">{record.day}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(record.status)}`}>
                      {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                    </span>
                    {record.status === 'present' && (
                      <p className="text-xs text-gray-500 mt-1">{record.checkIn} - {record.checkOut}</p>
                    )}
                    {record.note && (
                      <p className="text-xs text-gray-500 mt-1">{record.note}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subject-wise Attendance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Subject-wise Attendance</h2>
          </div>
          <div className="p-6 space-y-4">
            {subjectAttendance.map((subject, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{subject.subject}</span>
                  <span className="text-sm text-gray-500">{subject.attended}/{subject.total} ({subject.percentage}%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      subject.percentage >= 90 ? 'bg-green-500' :
                      subject.percentage >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${subject.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendance;
